// Keyboard-modality tracker — the one piece of Spectrum's focus system CSS
// cannot express (decided 2026-09-02).
//
// THE PROBLEM: browsers match `:focus-visible` on text inputs and textareas
// even when focus arrived by MOUSE, because typing is expected there. So a
// clicked text field drew both halves of the focus pair at once — the primary
// border (pointer focus) AND the blue-800 ring (keyboard focus): the "double
// blue border" defect. Selects never showed it only because Chromium does not
// extend that quirk to them. Adobe's own textfield gates its ring on a
// `.is-keyboardFocused` class set from a modality listener — the ring means
// "the keyboard is here", and only the keyboard may summon it.
//
// THE MECHANISM: one attribute on <html>, `data-ui-modality`, flipped between
// "kbd" and "pointer" by capture-phase listeners. tokens.css rings a text
// field on `:focus-visible` only while the attribute is NOT "pointer" — so a
// portal that never installs this keeps today's safe default (ring on every
// focus-visible), and an installed portal gets Spectrum's true behaviour.
// Buttons, checkboxes, radios and toggles stay on plain `:focus-visible`,
// which browsers already reserve for the keyboard on those controls.

/** Keys that mean "the keyboard is driving focus" — Tab and the arrows the
 *  roving-focus widgets use. Typing into a field must NOT flip the flag, or
 *  the first keystroke after a click would summon the ring mid-word. */
const NAV_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

let installed = false;

/**
 * Install the modality tracker once per document. Idempotent; returns an
 * uninstaller (used by tests — product shells never tear it down).
 * SSR-safe: a missing document is a no-op.
 */
export function installFocusModality(doc: Document | undefined = typeof document === "undefined" ? undefined : document): () => void {
  if (!doc || installed) return () => {};
  installed = true;
  const root = doc.documentElement;
  // Until the first interaction the attribute is "kbd": a page entered by Tab
  // from the URL bar must ring even though no tracked key was pressed inside.
  root.setAttribute("data-ui-modality", "kbd");

  const onKey = (e: KeyboardEvent) => {
    if (NAV_KEYS.has(e.key)) root.setAttribute("data-ui-modality", "kbd");
  };
  const onPointer = () => {
    root.setAttribute("data-ui-modality", "pointer");
  };
  doc.addEventListener("keydown", onKey, true);
  doc.addEventListener("pointerdown", onPointer, true);
  return () => {
    installed = false;
    doc.removeEventListener("keydown", onKey, true);
    doc.removeEventListener("pointerdown", onPointer, true);
    root.removeAttribute("data-ui-modality");
  };
}
